<script setup lang="ts">
import type { ScrubbedQuestion } from '@ilteducation/quizter-types';
import { storeToRefs } from 'pinia';
import { useQuizterStore } from '../stores/quizterStore';
import { nextTick, ref, watch } from 'vue';

type Props = {
  currentQuestion: ScrubbedQuestion;
  host?: boolean;
  hidePhraseTimer?: boolean;
};

const { gameOptions, questionCountdown } = storeToRefs(useQuizterStore());

const props = defineProps<Props>();

const headingRefHost = ref<HTMLElement | null>(null);
const headingRefPlayer = ref<HTMLElement | null>(null);
watch(
  [props.host],
  async () => {
    await nextTick();
    if (props.host) {
      headingRefHost.value?.focus();
    } else {
      headingRefPlayer.value?.focus();
    }
  },
  { immediate: true },
);
</script>

<template>
  <h1
    v-if="host"
    ref="headingRefHost"
    tabindex="-1"
    class="phrase chalkboard-font mb-10"
    data-testid="quizter-question-phrase"
  >
    {{ currentQuestion.phrase }}
  </h1>
  <div v-else class="d-sr-only">
    <h1 ref="headingRefPlayer" tabindex="-1">{{ currentQuestion.phrase }}</h1>
  </div>
  <div
    v-if="gameOptions.useQuestionShowTimer && !hidePhraseTimer"
    class="d-flex flex-column align-center justify-center mb-10"
  >
    <span aria-hidden="true">{{ Math.ceil(questionCountdown / 1000) }}</span>
    <v-progress-linear
      class="countdown-bar rounded"
      color="white"
      :model-value="questionCountdown"
      :max="gameOptions.questionShowTimeout"
      height="4"
      aria-hidden="true"
    ></v-progress-linear>
  </div>
</template>

<style scoped lang="scss">
.phrase {
  font-size: 1.5rem;
  text-align: center;
}
:deep(.countdown-bar .v-progress-linear__determinate) {
  transition: none !important;
}
[tabindex='-1']:focus {
  outline: none;
}
</style>
